import React from 'react';

import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';

import { postMessageToMobileApp } from 'components/app/helpers';
import { AVAILABLE_THEMES, IS_RN_WEBVIEW } from 'constants/constants';
import { OAUTH_PROVIDERS, POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import { noop } from 'helpers';
import { isNil } from 'helpers/ramda';
import { Box } from 'UIKit';
import DeltaButton from 'UIKit/DeltaButton';

import { OAuthButtonProps } from './interface';
import classes from './oauth.module.scss';
import OAuthIconWrapper from './OAuthIconWrapper';

const GoogleButton = ({
  handleOAuthSignInSuccess,
  errorMessage = '',
  provider,
  disableButton = false,
  loading = false,
  onClickOAuthButton = noop,
}: OAuthButtonProps) => {
  const { t } = useTranslation(['auth']);

  const handleSuccess = tokenResponse => {
    handleOAuthSignInSuccess({
      accessToken: tokenResponse.access_token,
      provider: OAUTH_PROVIDERS.GOOGLE,
    });
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: handleSuccess,
    onError: noop,
  });

  const handleClick = e => {
    e.preventDefault();
    onClickOAuthButton(OAUTH_PROVIDERS.GOOGLE);
    if (IS_RN_WEBVIEW) {
      postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.OAUTH_BUTTON_CLICKED, {
        provider: OAUTH_PROVIDERS.GOOGLE,
      });
    } else {
      loginWithGoogle();
    }
  };

  return (
    <Box className={classes.buttonWrapper}>
      <DeltaButton
        data-testid="GoogleSignInButton"
        fullWidth
        onClick={handleClick}
        onKeyDown={noop}
        className={classes.signInWithGoogleBtn}
        disabled={disableButton}
        loading={loading && provider === OAUTH_PROVIDERS.GOOGLE}
      >
        <OAuthIconWrapper
          color={AVAILABLE_THEMES.LIGHT}
          provider={OAUTH_PROVIDERS.GOOGLE}
        />
        <div className={classes.buttonLabel}>{t('auth:signInWithGoogle')}</div>
      </DeltaButton>

      <Box
        renderWhen={!isNil(errorMessage) && provider === OAUTH_PROVIDERS.GOOGLE}
        className={classes.errorMessage}
      >
        {errorMessage}
      </Box>
    </Box>
  );
};

GoogleButton.displayName = 'GoogleButton';

export default GoogleButton;
